import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// MUI Components
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import PopAddEditHotspot from '../../components/Space/PopAddEditHotspot';
import PopAddEditHistory from '../../components/Space/PopAddEditHistory';
import PopLockNorth from '../../components/Space/PopLockNorth';
import PopLanguages from '../../components/PopLanguages';
import PopNarations from '../../components/PopNarations';

import { ASSETS_PATH } from '../../constants';
import { useParams } from 'react-router-dom';
import CircularWithValueLabel from '../../components/CircularWithValueLabel';
import Swal from 'sweetalert2';
import axiosClient from '../../services/axios-client';

import { HiMiniLanguage } from "react-icons/hi2";
import QRCode from "react-qr-code";
import { iSpace, iSpaceHotspot, iSpaceHistory } from '../../models/Space';
import { iLocation } from '../../models/Location';

const Space: React.FC = () => {

    const [editHotspot, setEditHotspot] = useState<iSpaceHotspot | null>(null)
    const [editHistory, setEditHistory] = useState<iSpaceHistory | null>(null);
    const [editNorth, setEditNorth] = useState<boolean>(false); // to open lock north popup
    const { space_id } = useParams<{ space_id: string }>();
    const [/** */, setSpaceId] = useState<number>();
    const [spaces, setSpaces] = useState<iSpace[]>([]);
    const [spaceName, setSpaceName] = useState<string>("");
    const [spaceNameLs, setSpaceNameLs] = useState<any>({})
    const [spaceDefaultYear, setSpaceDefaultYear] = useState<string>("");
    const [spaceDefaultYearLs, setSpaceDefaultYearLs] = useState<any>({})
    const [spaceIdentifier, setSpaceIdentifier] = useState<string>("");
    const [spaceBeaconId, setSpaceBeaconId] = useState<string>("");
    const [spaceBeaconRSSI, setSpaceBeaconRSSI] = useState<string | number>("");
    const [locationRSSI, setLocationRSSI] = useState<string | number>("");
    const [locationId, setLocationId] = useState<number>(0);
    const [locations, setLocations] = useState<iLocation[]>([]);
    const [spaceImage, setSpaceImage] = useState<string>("");
    const [spaceVideo, setSpaceVideo] = useState<string>("");
    const [spaceBackgroundSound, setSpaceBackgroundSound] = useState<string>("");
    const [hotspots, setHotspots] = useState<iSpaceHotspot[]>([]);
    const [history, setHistory] = useState<iSpaceHistory[]>([]);
    const [uploading, setUploading] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);

    const [langPop, setLangPop] = useState<boolean>(false);
    const [langPopDefaultYear, setLangPopDefaultYear] = useState<boolean>(false);
    const [northDirection, setNorthDirection] = useState<number>(0);

    const [narrations, setNarrations] = useState<any>(null);
    const [narrationsExtended, setNarrationsExtended] = useState<any>(null);
    const [narrationsPop, setNarrationsPop] = useState<boolean>(false);
    const [narrationsExtendedPop, setNarrationsExtendedPop] = useState<boolean>(false);

    const [errors, setErrors] = useState<any>({});



    const onChangeUploadSpaceImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validation - File Type
        const validTypes = ['image/jpeg', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            setErrors((prev: any) => ({ ...prev, spaceImage: 'Only JPG or JPEG images are allowed.' }));
            return;
        }

        // Validation - File Size (< 15MB)
        if (file.size > 15 * 1024 * 1024) {
            setErrors((prev: any) => ({ ...prev, spaceImage: 'File size should be below 15MB.' }));
            return;
        }

        // Validation - Image Ratio (2:1)
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            const ratio = img.naturalWidth / img.naturalHeight;
            if (Math.abs(ratio - 2) > 0.05) { // Allowing a small margin for error
                setErrors((prev: any) => ({ ...prev, spaceImage: 'Image ratio should be 2:1.' }));
                URL.revokeObjectURL(img.src);
                return;
            }
            // If all valid
            setErrors((prev: any) => ({ ...prev, spaceImage: null }));
            uploadAsset(e, (fileName: string) => { setSpaceImage(fileName) });
            URL.revokeObjectURL(img.src);
        };
    }

    const onChangeUploadSpaceVideo = (e: React.ChangeEvent<HTMLInputElement>) => {
        uploadAsset(e, (fileName: string) => {
            setSpaceVideo("");
            setTimeout(() => { setSpaceVideo(fileName) }, 1000);
        });


    }

    const onChangeUploadSpaceBackgroundSound = (e: React.ChangeEvent<HTMLInputElement>) => {
        uploadAsset(e, (fileName: string) => { setSpaceBackgroundSound(fileName) });
    }

    const onRemoveSpaceBackgroundSound = () => {
        setSpaceBackgroundSound("");
    }


    useEffect(() => {
        if (space_id) {
            fetch(`${process.env.REACT_APP_API_URL}/space`, { method: 'GET' })
                .then((res) => res.json())
                .then((json) => {
                    setSpaces(json.spaces);
                    const currentSpace = json.spaces.find((space: iSpace) => space.space_id === parseInt(space_id));
                    if (currentSpace) {
                        setPageData(currentSpace);
                    }
                });
        }
    }, [space_id])

    useEffect(() => {
        axiosClient.get('/locations')
            .then(({ data }) => {
                setLocations(data.locations);
            })
            .catch(err => {
                console.error(err);
            });
    }, [])



    const setPageData = (space: iSpace) => {

        setSpaceId(space.space_id);
        setSpaceName(space.space_name);
        setSpaceNameLs(space.space_name_ls || {})
        setSpaceDefaultYear(space.space_default_year || "");
        setSpaceDefaultYearLs(space.space_default_year_ls || {})
        setSpaceIdentifier(space.space_identifier);
        setSpaceBeaconId(space.space_beacon_id);
        setSpaceImage(space.space_image);
        setSpaceVideo(space.space_video);
        setSpaceBackgroundSound(space.space_background_sound);
        setNorthDirection(space.space_north_direction);
        setSpaceBeaconRSSI(space.space_beacon_rssi);
        setLocationRSSI(space.location_rssi);
        setLocationId(space.location_id || 0);

        if (space.space_audio) {
            setNarrations(space.space_audio);
        } else {
            setNarrations({});
        }

        if (space.space_audio_extended) {
            setNarrationsExtended(space.space_audio_extended);
        } else {
            setNarrationsExtended({});
        }

        if (space.space_hotspots) {
            setHotspots(space.space_hotspots);
        }

        if (space.space_history) {
            setHistory(space.space_history);
        }

    }


    const nextHotspotId = () => {

        if (hotspots.length === 0) return 1;

        const ids: number[] = [];

        hotspots.forEach(ht => {
            ids.push(ht.hotspot_id);
        });

        return Math.max(...ids) + 1;
    }


    const nextEraId = () => {

        if (history.length === 0) return 1;

        const ids: number[] = [];

        history.forEach(ht => {
            ids.push(ht.era_id);
        });

        return Math.max(...ids) + 1;
    }


    const uploadAsset = async (e: React.ChangeEvent<HTMLInputElement>, cb: (fileName: string) => void) => {

        if (!e.target.files || e.target.files.length === 0) return;

        setUploading(true);

        let formData = new FormData();
        formData.append("file", e.target.files[0]);

        axiosClient.post(`${process.env.REACT_APP_API_URL}/upload-file`, formData, {
            headers: {
                "Content-Type": "multipart/form-data"
            },
            onUploadProgress: event => {
                if (event.total) {
                    setProgress(100 * event.loaded / event.total);
                }
            }
        }).then((res) => {
            cb(res.data.filename);
            setUploading(false);
        })

    }


    const onChangeSpaceName = (e: React.ChangeEvent<HTMLInputElement>) => {
        const s = e.target.value;
        setSpaceName(s);
    }

    const onChangeSpaceDefaultYear = (e: React.ChangeEvent<HTMLInputElement>) => {
        const s = e.target.value;
        setSpaceDefaultYear(s);
    }

    const onChangeSpaceIdentifier = (e: React.ChangeEvent<HTMLInputElement>) => {
        const s = e.target.value;
        setSpaceIdentifier(s);
    }


    const onChangeSpaceBeaconId = (e: React.ChangeEvent<HTMLInputElement>) => {
        const s = e.target.value;
        setSpaceBeaconId(s);
    }

    const onChangeSpaceBeaconRSSI = (e: React.ChangeEvent<HTMLInputElement>) => {
        const s = e.target.value;
        setSpaceBeaconRSSI(s);
    }

    const onChangeLocationRSSI = (e: React.ChangeEvent<HTMLInputElement>) => {
        const s = e.target.value;
        setLocationRSSI(s);
    }

    const onEditHotspot = (hotspot: iSpaceHotspot) => {

        setEditHotspot({ ...hotspot });

    }

    const onDeleteHotspot = (index: number) => {
        const h = [...hotspots];
        h.splice(index, 1);
        setHotspots(h);
    }

    const updateHotspot = (hotspot: iSpaceHotspot) => {
        let hsps = [...hotspots];
        let index = hotspots.findIndex((h) => h.hotspot_id === hotspot.hotspot_id);

        if (index < 0) {
            hsps.push(hotspot);
        } else {
            hsps[index] = hotspot;
        }


        setHotspots(hsps);
        setEditHotspot(null);
    }


    const onDone = () => {
        setEditHotspot(null);
    }

    const onAddHotspot = () => {
        const h: iSpaceHotspot = {
            hotspot_id: nextHotspotId(),
            hotspot_name: "",
            hotspot_type: "none",
            geners: [],
            hotspot_coords: { x: 0, y: 0, z: 0 },
            hotspot_data: {
                hotspot_linked_to: 0,
                title: "",
                description: "",
                narration_url: "",
                gallery: [],
                artefact: {
                    model_url: "",
                    model_map_url: ""
                }
            }
        };
        setEditHotspot(h)
    }


    const onEditHistory = (era: iSpaceHistory) => {
        setEditHistory(era);
    }

    const onDeleteHistory = (index: number) => {
        const h = [...history];
        h.splice(index, 1);
        setHistory(h);
    }

    const onHistoryDone = () => {
        setEditHistory(null);
    }

    const onUpdateHistory = (era: iSpaceHistory) => {

        let eras = [...history];
        let index = eras.findIndex((e) => e.era_id === era.era_id);

        if (index < 0) {
            eras.push(era);
        } else {
            eras[index] = era;
        }


        setHistory(eras);
        setEditHistory(null);

    }


    const onOpenNorthPopup = () => {
        setEditNorth(true);
    }

    const onCloseNorthPopup = () => {
        setEditNorth(false);
    }


    const onClickAddNewEra = () => {
        setEditHistory({ era_id: nextEraId(), asset_url: "", year: "" });
    }


    const validate = () => {
        const newErrors: any = {};

        // Space Name
        if (!spaceName) {
            newErrors.spaceName = "Space Name is mandatory.";
        } else if (!/^[a-zA-Z0-9 ]+$/.test(spaceName)) {
            newErrors.spaceName = "Only alphanumeric characters and spaces allowed.";
        } else if (spaceName.length < 3) {
            newErrors.spaceName = "Minimum 3 characters required.";
        }

        // Space Belongs To
        if (locationId === 0) {
            newErrors.locationId = "Space Belongs To is mandatory.";
        }

        // Space Identifier
        if (!spaceIdentifier) {
            newErrors.spaceIdentifier = "Space Identifier is mandatory.";
        } else if (!/^[a-zA-Z0-9 ]+$/.test(spaceIdentifier)) {
            newErrors.spaceIdentifier = "Only alphanumeric characters and spaces allowed.";
        } else if (spaceIdentifier.length < 3) {
            newErrors.spaceIdentifier = "Minimum 3 characters required.";
        }

        // Beacon ID
        if (!spaceBeaconId) {
            newErrors.spaceBeaconId = "Beacon ID is mandatory.";
        } else if (spaceBeaconId !== "null") {
            if (!/^[A-Z0-9]{12}$/.test(spaceBeaconId)) {
                newErrors.spaceBeaconId = "Must be 12 alphanumeric characters (ALL CAPS) or 'null'.";
            }
        }

        // Beacon RSSI
        const rssi = parseInt(spaceBeaconRSSI.toString());
        if (!spaceBeaconRSSI && spaceBeaconRSSI !== 0) {
            newErrors.spaceBeaconRSSI = "Beacon RSSI is mandatory.";
        } else if (isNaN(rssi) || rssi < -100 || rssi > -50) {
            newErrors.spaceBeaconRSSI = "Value should be between -100 to -50.";
        }

        // Location RSSI
        const locRssi = parseInt(locationRSSI.toString());
        if (!locationRSSI && locationRSSI !== 0) {
            newErrors.locationRSSI = "Location RSSI is mandatory.";
        } else if (isNaN(locRssi)) {
            newErrors.locationRSSI = "Value must be a number.";
        } else if (!isNaN(rssi) && locRssi >= rssi - 19) {
            newErrors.locationRSSI = `Value should be less than Beacon RSSI - 20 (${rssi - 20}).`;
        }

        // Space Image
        if (!spaceImage) {
            newErrors.spaceImage = "Space Image is mandatory.";
        }

        // Default Year
        if (!spaceDefaultYear) {
            newErrors.spaceDefaultYear = "Default Year is mandatory.";
        } else if (!/^\d{4}$/.test(spaceDefaultYear)) {
            newErrors.spaceDefaultYear = "Must be exactly 4 digits (numbers only).";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const onSaveSpace = async () => {

        if (!validate()) {
            Swal.fire('Error', 'Please fix the errors before saving.', 'error');
            return;
        }

        try {
            const payload = {
                space_id: space_id,
                space_name: spaceName,
                space_name_ls: JSON.stringify(spaceNameLs),
                space_default_year: spaceDefaultYear,
                space_default_year_ls: JSON.stringify(spaceDefaultYearLs),
                space_identifier: spaceIdentifier,
                space_beacon_id: spaceBeaconId,
                space_beacon_rssi: spaceBeaconRSSI,
                location_rssi: locationRSSI,
                location_id: locationId,
                space_north_direction: northDirection,
                space_image: spaceImage,
                space_video: spaceVideo,
                space_background_sound: spaceBackgroundSound,
                space_audio: JSON.stringify(narrations),
                space_audio_extended: JSON.stringify(narrationsExtended),
                space_hotspots: JSON.stringify(hotspots),
                space_history: JSON.stringify(history)
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/space`, {
                method: "PUT",
                body: JSON.stringify(payload),
                headers: {
                    "Content-Type": "application/json",
                }
            });

            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.message || 'Failed to save space');
            }

            Swal.fire('Saved!', json.message || 'Space has been saved successfully.', 'success');
        } catch (err) {
            console.error(err);
            Swal.fire('Error!', err instanceof Error ? err.message : 'An unknown error occurred.', 'error');
        }
    }

    const onRemoveSpaceImage = () => {
        setSpaceImage("");
    }


    const onRemoveSpaceVideo = () => {
        setSpaceVideo("");
    }

    const onSpaceNameLangPop = () => {
        setLangPop(true);
    }

    const onSpaceNameLangPopClose = () => {
        setLangPop(false);
    }

    const onSetSpaceNameLs = (langs: any) => {
        setSpaceNameLs((prevLs: any) => { return { ...langs } })
        setLangPop(false);
    }

    const onSpaceDefaultYearLangPop = () => {
        setLangPopDefaultYear(true);
    }

    const onSpaceDefaultYearLangPopClose = () => {
        setLangPopDefaultYear(false);
    }

    const onSetSpaceDefaultYearLs = (langs: any) => {
        setSpaceDefaultYearLs((prevLs: any) => { return { ...langs } })
        setLangPopDefaultYear(false);
    }


    const onSetNarrations = (narrations: any) => {
        setNarrations(narrations);
        setNarrationsPop(false);
    }

    const onNarrationsPopClose = () => {
        setNarrationsPop(false);
    }


    const onNarrPopup = () => {
        setNarrationsPop(true);
    }

    const onSetNarrationsExtended = (narrations: any) => {
        setNarrationsExtended(narrations);
        setNarrationsExtendedPop(false);
    }

    const onNarrationsExtendedPopClose = () => {
        setNarrationsExtendedPop(false);
    }


    const onNarrExtendedPopup = () => {
        setNarrationsExtendedPop(true);
    }

    return (
        <>
            <div className="content-body">
                <form className='flex flex-row items-start gap-8'>
                    <div className="flex flex-col gap-8 flex-1">
                        <div className="flex flex-row gap-4 w-full">
                            <div className='flex flex-row flex-1'>
                                <TextField
                                    className='textfield w-full'
                                    id="space-name"
                                    label="Space Name"
                                    value={spaceName}
                                    onChange={onChangeSpaceName}
                                    error={!!errors.spaceName}
                                    helperText={errors.spaceName}
                                />
                                <IconButton aria-label="Add space name in other languages" onClick={onSpaceNameLangPop}>
                                    <HiMiniLanguage className='l-icon' />
                                </IconButton>
                            </div>

                            <FormControl sx={{ width: '250px' }} error={!!errors.locationId}>
                                <InputLabel id="location-select-label">Space Belongs To</InputLabel>
                                <Select
                                    labelId="location-select-label"
                                    id="location-select"
                                    value={locationId}
                                    label="Space Belongs To"
                                    onChange={(e: SelectChangeEvent<number>) => setLocationId(e.target.value as number)}
                                >
                                    <MenuItem value={0}>Select Location</MenuItem>
                                    {locations.map((loc) => (
                                        <MenuItem key={loc.location_id} value={loc.location_id}>
                                            {loc.location_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.locationId && <FormHelperText>{errors.locationId}</FormHelperText>}
                            </FormControl>
                        </div>

                        <div className="grid grid-cols-4 gap-4 w-full">
                            <TextField
                                label="Space Identifier"
                                className='textfield w-full'
                                id="space-identifier"
                                value={spaceIdentifier}
                                onChange={onChangeSpaceIdentifier}
                                error={!!errors.spaceIdentifier}
                                helperText={errors.spaceIdentifier || 'If you changing identifier, Make sure QR code image need to be updated as well if already created.'}
                            />

                            <TextField
                                label="Beacon ID"
                                className='textfield w-full'
                                id="space-beacon-id"
                                value={spaceBeaconId}
                                onChange={onChangeSpaceBeaconId}
                                error={!!errors.spaceBeaconId}
                                helperText={errors.spaceBeaconId || 'You can find the beacon ID on the back side of the Beacon device.'}
                            />

                            <TextField
                                label="Beacon RSSI"
                                className='textfield w-full'
                                type="number"
                                slotProps={{
                                    htmlInput: {
                                        min: -100,
                                        max: -50
                                    }
                                }}
                                id="space-beacon-rssi"
                                value={spaceBeaconRSSI}
                                onChange={onChangeSpaceBeaconRSSI}
                                error={!!errors.spaceBeaconRSSI}
                                helperText={errors.spaceBeaconRSSI || 'Minimum RSSI value required to trigger space. -50 is strongest signal. RSSI range is -50 to -100.'}
                            />

                            <TextField
                                label="Location RSSI"
                                className='textfield w-full'
                                type="number"
                                slotProps={{
                                    htmlInput: {
                                        min: -100,
                                        max: -50
                                    }
                                }}
                                id="location-rssi"
                                value={locationRSSI}
                                onChange={onChangeLocationRSSI}
                                error={!!errors.locationRSSI}
                                helperText={errors.locationRSSI || 'Minimum RSSI value required to trigger location change. -50 is strongest signal. RSSI range should be more than space RSSI value.'}
                            />
                        </div>


                        <Card variant="outlined" className="w-full">
                            <CardContent className="flex flex-col gap-4">
                                <Typography variant="h6">Space Image</Typography>
                                <div className="flex flex-col items-start gap-4">
                                    {spaceImage && <img src={`${ASSETS_PATH}/${spaceImage}`} width={400} alt="Space" className="rounded border" />}

                                    {!spaceImage && <div className="flex flex-col gap-1">
                                        <Button
                                            variant="contained"
                                            component="label"
                                            color={errors.spaceImage ? "error" : "primary"}
                                        >
                                            Upload Image
                                            <input
                                                type="file"
                                                hidden
                                                onChange={onChangeUploadSpaceImage}
                                            />
                                        </Button>
                                        {errors.spaceImage && <Typography color="error" variant="caption">{errors.spaceImage}</Typography>}
                                    </div>}
                                    {spaceImage && <div className='flex flex-row gap-4'>
                                        <Button variant="contained" onClick={onOpenNorthPopup}>Lock North</Button>
                                        <Button variant="outlined" color="error" onClick={onRemoveSpaceImage}>Remove Image</Button>
                                    </div>}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Upload Video section */}
                        {false && spaceVideo && <video width={400} controls autoPlay id="spaceVideo">
                            <source src={`${ASSETS_PATH}/${spaceVideo}`} type="video/mp4" />
                        </video>}
                        {false && !spaceVideo && <Button
                            variant="contained"
                            component="label"
                        >
                            Upload Video
                            <input
                                type="file"
                                hidden
                                onChange={onChangeUploadSpaceVideo}
                            />
                        </Button>}
                        {false && spaceVideo && <Button onClick={onRemoveSpaceVideo}>Remove Video</Button>}


                        <Card variant="outlined" className="w-full">
                            <CardContent className="flex flex-col gap-4">
                                <div className="flex flex-row items-center justify-between w-full">
                                    <Typography variant="h6">Hotspots</Typography>
                                    <Button variant="contained" onClick={onAddHotspot}>Add Hotspot</Button>
                                </div>

                                <table className="table-auto border w-full">
                                    <thead>
                                        <tr>
                                            <th className="font-bold p-2 border-b text-left w-2">No</th>
                                            <th className="font-bold p-2 border-b text-left">Hotspot Name</th>
                                            <th className="font-bold p-2 border-b text-left">Hotspot Type</th>
                                            <th className="font-bold py-2 px-4 border-b text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>

                                        {hotspots.map((hotspot, index) => <tr key={index}>
                                            <td className="p-2 border-b text-left">{index + 1}</td>
                                            <td className="p-2 border-b text-left">{hotspot.hotspot_name}</td>
                                            <td className="p-2 border-b text-left">{hotspot.hotspot_type[0].toUpperCase() + hotspot.hotspot_type.slice(1)}</td>
                                            <td className="py-2 px-4 border-b text-left">
                                                <Button onClick={() => { onEditHotspot(hotspot) }}>Edit</Button>
                                                <Button onClick={() => { onDeleteHotspot(index) }}>Delete</Button>
                                            </td>
                                        </tr>)}

                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>

                        <Card variant="outlined" className="w-full">
                            <CardContent className="flex flex-col gap-4">
                                <Typography variant="h6">History</Typography>
                                <div className="flex flex-row items-center justify-between gap-4 w-full">
                                    <div className='flex flex-row'>
                                        <TextField
                                            className='textfield'
                                            sx={{ width: '150px' }}
                                            id="space-default-year"
                                            label="Default Year"
                                            value={spaceDefaultYear}
                                            onChange={onChangeSpaceDefaultYear}
                                            error={!!errors.spaceDefaultYear}
                                            helperText={errors.spaceDefaultYear}
                                        />
                                        <IconButton aria-label="Add default year in other languages" onClick={onSpaceDefaultYearLangPop}>
                                            <HiMiniLanguage className='l-icon' />
                                        </IconButton>
                                    </div>
                                    <Button variant="contained" onClick={onClickAddNewEra}>Add New Year</Button>
                                </div>

                                <table className="table-auto border w-full">
                                    <thead>
                                        <tr>
                                            <th className="font-bold p-2 border-b text-left w-2">Year</th>
                                            <th className="font-bold p-2 border-b text-left">Preview</th>
                                            <th className="font-bold py-2 px-4 border-b text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>

                                        {history.map((era, index) => <tr key={era.year}>
                                            <td className="p-2 border-b text-left">{era.year}</td>
                                            <td className="p-2 border-b text-left"><img src={`${ASSETS_PATH}/${era.asset_url}`} width={150} alt={`${era.year}`} /></td>
                                            <td className="py-2 px-4 border-b text-left">
                                                <Button onClick={() => { onEditHistory(era) }}>Edit</Button>
                                                <Button onClick={() => { onDeleteHistory(index) }}>Delete</Button>
                                            </td>
                                        </tr>)}

                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="flex flex-col gap-4 w-[300px] sticky top-0">
                        <div className="flex flex-row gap-4 w-full">
                            <Button variant="contained" color="primary" className="flex-1" onClick={onSaveSpace}>Save</Button>
                            <Button variant="outlined" className="flex-1">Cancel</Button>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="text-gray-400 text-[10px] uppercase font-bold border-b border-gray-100 pb-1 mt-2">QR Code</div>
                            <div className="flex flex-col items-center justify-center p-4 border rounded bg-white">
                                <QRCode
                                    size={250}
                                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                    value={JSON.stringify({ "page": "tour", "space": spaceName })}
                                    viewBox={`0 0 250 250`}
                                />
                            </div>
                            <div className="text-gray-500 text-[11px] text-center italic">Scan QR code in App to open this Space.</div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="text-gray-400 text-[10px] uppercase font-bold border-b border-gray-100 pb-1 mt-2">Space Audio</div>
                            <Button variant='contained' onClick={onNarrPopup}>Audio About Space</Button>
                            <Button variant='contained' onClick={onNarrExtendedPopup}>Extended Audio About Space</Button>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="text-gray-400 text-[10px] uppercase font-bold border-b border-gray-100 pb-1 mt-2">Background Sound</div>
                            {spaceBackgroundSound && <audio controls className="w-full">
                                <source src={`${ASSETS_PATH}/${spaceBackgroundSound}`} type="audio/mpeg" />
                            </audio>}

                            {
                                !spaceBackgroundSound && <Button variant="contained" component="label">
                                    Upload Sound
                                    <input
                                        type="file"
                                        hidden
                                        onChange={onChangeUploadSpaceBackgroundSound}
                                    />
                                </Button>
                            }

                            {spaceBackgroundSound && <Button variant="outlined" color="error" size="small" onClick={onRemoveSpaceBackgroundSound}>Remove Sound</Button>}
                        </div>
                    </div>
                </form>

                {
                    editHotspot &&
                    <PopAddEditHotspot
                        updateHotspot={updateHotspot}
                        hotspot={editHotspot}
                        onDone={onDone}
                        spaces={spaces}
                        spaceImage={spaceImage}
                        uploadAsset={uploadAsset}
                    />}

                {
                    editHistory &&
                    <PopAddEditHistory
                        era={editHistory}
                        onHistoryDone={onHistoryDone}
                        onUpdateHistory={onUpdateHistory}
                        uploadAsset={uploadAsset}
                    />
                }
                {
                    editNorth &&
                    <PopLockNorth
                        onClose={onCloseNorthPopup}
                        northDirection={northDirection}
                        setNorthDirection={setNorthDirection}
                        spaceImage={spaceImage}
                    />
                }
            </div>
            {uploading && <CircularWithValueLabel progress={progress} />}
            {langPop && createPortal(<PopLanguages fieldName={`Space Name`} languages={spaceNameLs} onSetLs={onSetSpaceNameLs} onLangPopClose={onSpaceNameLangPopClose} />, document.getElementById('modal') as HTMLElement)}
            {langPopDefaultYear && createPortal(<PopLanguages fieldName={`Default Year`} languages={spaceDefaultYearLs} onSetLs={onSetSpaceDefaultYearLs} onLangPopClose={onSpaceDefaultYearLangPopClose} />, document.getElementById('modal') as HTMLElement)}
            {narrationsPop && createPortal(<PopNarations narrations={narrations} onSetNarrations={onSetNarrations} onNarrationsPopClose={onNarrationsPopClose} />, document.getElementById('modal') as HTMLElement)}
            {narrationsExtendedPop && createPortal(<PopNarations narrations={narrationsExtended} onSetNarrations={onSetNarrationsExtended} onNarrationsPopClose={onNarrationsExtendedPopClose} />, document.getElementById('modal') as HTMLElement)}
        </>
    )
}

export default Space;
